<script lang="ts">
    import { computed, onMounted, reactive, ref, Ref, watchEffect, onUnmounted, watch, toRaw, inject } from 'vue';
    import vm, { exitToStartMenu, saveCurrentJourney, ownedObjects, UngappedJourney, resetActiveJourney, Journey } from '@journeys/Journeys.viewmodel';

    // assets
    import { staticPresetsStart, staticPresetsActions, staticPresetsConditions } from './UserInterface/Menues/Partials/NodeStaticPresets';

    // components
    import UserInterface from './UserInterface/UserInterface.vue';
    import CanvasMain from './Canvas/CanvasMain.vue';

    import ModalContainer from '@shared/components/ModalContainer.vue';
    import NodePresetList from '@journeys/components/UserInterface/Menues/Partials/NodePresetList.vue';
    import ContextMenuContainer from '@shared/containers/ContextMenuContainer.vue';

    // !! to be depricated (replace by ContextMenuContainer that accepts menu component through event emit)
    import ContextMenu from '@shared/components/ContextMenu.vue';

    // utils
    import { /*bitshift,*/ debounce, /*deepClone,*/ requestFrame } from '@utils';
    // import { api } from '@cwd/lib/main'
    import { HistoryClass } from '@shared/utils/IterationControllers';
    import { versionManager } from '@journeys/utils/VersionManager';
    import { api } from '@cwd/lib/main';
    import { JourneyStatus } from '@journeys/Enums';
    import { useRouter } from 'vue-router';
    import { getFeatureSets } from '@backoffice/FeatureSets/FeatureSets.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SegmentAccess } from '@cwd/Views/Segments/Segments.access';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'JourneyEditor_Viewport',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const segmentAccess: SegmentAccess = injectorAccess.injector.get(SegmentAccess);

    const props = defineProps<{
        JourneyId: string;
    }>();

    const state = reactive<{
        hasLoaded: boolean;
    }>({
        hasLoaded: false,
    });
    const router = useRouter();
    const { viewport, /*settings,*/ nav, /*canvas,*/ editor, /*account,*/ activeJourney, account } = vm,
        viewportRef = ref<HTMLElement>();

    const handleKeyboardShortcuts = (ev: KeyboardEvent) => {
        if (!(document.activeElement === null || document.activeElement === document.body)) return;

        // zoom in
        if (ev.altKey && ev.key === '+') {
            ev.preventDefault();
            ev.stopPropagation();

            nav.z = nav.z + 100 > nav.zmin ? nav.zmin : nav.z + 100;
        }

        // zoom out
        if (ev.altKey && ev.key === '-') {
            ev.preventDefault();
            ev.stopPropagation();

            nav.z = nav.z - 100 < nav.zmax ? nav.zmax : nav.z - 100;
        }

        // delete
        if (ev.code === 'Delete' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('deleteselection');
        }

        // redo
        if (ev.ctrlKey && ev.code === 'KeyY' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('historyredo');
        }

        // undo
        if (ev.ctrlKey && ev.code === 'KeyZ' && document.activeElement === document.body) {
            ev.preventDefault();
            ev.stopPropagation();

            editor.events.emit('historyundo');
        }
    };

    const history = new HistoryClass<UngappedJourney>(activeJourney, {
        ignore: ['JourneyId', 'runtime', 'content', 'ownedObjects', 'scheduleController', 'steps', 'startingStep'],
        fullProps: ['points', 'Segments', 'Tags'],
        // events: editor.events,
    });

    const clearSelection = () => {
        editor.tools.sel_nodes = {};
        editor.tools.sel_points = {};
        editor.tools.sel_lines = {};
        editor.tools.sel_keyObj = null;
    };

    editor.events.addListener('clearselection', clearSelection);

    const handleHistoryStep = (step: ReturnType<typeof history.next>) => {
        if (!step) return;

        clearSelection();
        // step.deleted ? clearSelection() : editor.events.emit("calcselarea")

        for (let line of step.newLines) editor.events.emit('initline', line);

        debounce(
            () => {
                // debounce hitbox generation
                for (let id in activeJourney.lines) editor.events?.emit('newlinehitboxes', activeJourney.lines[id]);

                history.watcher?.muteUpdate();
            },
            {},
            1
        );
    };

    editor.events.addListener('historyredo', () => handleHistoryStep(history.next()));
    editor.events.addListener('historyundo', () => handleHistoryStep(history.prev()));
    editor.events.addListener('historyreset', () => history.reset());

    onMounted(() => {
        viewport.el = viewportRef.value;
        viewport.rect = viewport.el!.getBoundingClientRect();
        viewport.quickmenu.container = viewport.dims;
        viewport.quickmenu.containerRect = viewport.rect;
        viewport.contextmenu.container = viewport.dims;
        viewport.contextmenu.containerRect = viewport.rect;

        viewport.updateData = () => {
            viewport.dims.h = viewport.el!.offsetHeight;
            viewport.dims.w = viewport.el!.offsetWidth - (editor.state.showPropertyWindow ? viewport.computed.value.raw_pws : 0);
        };

        viewport.updateData();
        window.addEventListener('resize', viewport.updateData);

        editor.events.addListener('savecurrent', saveCurrentJourney);
        editor.events.addListener('saveandexit', exitToStartMenu);

        window.addEventListener('keydown', handleKeyboardShortcuts);
    });

    onUnmounted(() => {
        editor.persistantState.disposeAll();
        history.dispose();
        window.removeEventListener('keydown', handleKeyboardShortcuts);
    });

    const { quickmenu, contextmenu } = viewport,
        pauseTransition = ref(false);
    const openquickmenu = (ev: MouseEvent, val: any) => {
        pauseTransition.value = true;

        viewport.rect = viewport.el!.getBoundingClientRect();

        let evt = ev.target as HTMLElement;
        if (evt.dataset.nodetype === 'canvas-node') {
            contextmenu.show = val;
            contextmenu.x = ev.x - viewport.rect.x;
            contextmenu.y = ev.y - viewport.rect.y;
            contextmenu.nodeId = evt.dataset.id!;
        } else {
            quickmenu.show = val;
            quickmenu.x = ev.x - viewport.rect.x;
            quickmenu.y = ev.y - viewport.rect.y;
        }

        requestFrame(() => (pauseTransition.value = false));
    };

    // TODO: WIP replacement for quickmenu & contextmenu
    const opentContextMenu = (ev: MouseEvent, val: boolean) => {
        let evt = ev.target as HTMLElement;
        if (evt.dataset.nodetype === 'canvas-node') {
        } else {
        }
    };

    editor.persistantState.setup(vm.activeJourney.JourneyId!, {
        nav: {
            target: nav,
            watch_props: () => ({ x: nav.x, y: nav.y, z: nav.z }),
        },
    });

    onMounted(() => {
        window.addEventListener('keydown', (ev: KeyboardEvent) => {
            if (ev.code === 'KeyW') {
                console.log(history.bidi.data);
            }
        });
    });
    const setupListings = () => {
        // TODO: syntax....
        account.listings.add('/tags', { map_key: 'TagId', eager: true });
        account.listings.add('/textmessages/listing', { map_key: 'SmsMessageId', eager: true });
        account.listings.add('/lists', { map_key: 'ListId' });
        account.listings.add('/segments/listing', { map_key: 'SegmentId' });
        account.listings.add('/issues/listing', { map_key: 'IssueId' });
        account.listings.add('/surveys/listing', { map_key: 'SurveyId', eager: true });
        account.listings.add('/events/listing', { map_key: 'EventId' });
    };
    onMounted(() => {
        activeJourney.JourneyId = Array.isArray(router.currentRoute.value.params.JourneyId)
            ? router.currentRoute.value.params.JourneyId[0]
            : router.currentRoute.value.params.JourneyId;
        activeJourney.Title = $Resources.CleanJourneyTitle;
        activeJourney.Status = JourneyStatus.Draft;

        api.get(`/journeys/${router.currentRoute.value.params.JourneyId}`).then((res: any) => {
            if (res) {
                let data = res.GraphicalData ? JSON.parse(res.GraphicalData) : activeJourney,
                    op_data = JSON.parse(res.OperationalData),
                    op_rootProps = ['JourneyId', 'steps', 'ownedObjects', 'startDateTime', 'scheduleController'];

                data.Status = res.Status;

                for (let key of op_rootProps) if (op_data[key]) data[key] = op_data[key];

                for (let key in data.nodes) {
                    op_data?.nodes && op_data.nodes[key] && Object.assign(data.nodes[key].data, op_data.nodes[key]);
                    data.nodes[key].runtime ||= {};
                }

                data.Tags = res.Tags;

                Object.assign(vm.activeJourney, data);
                Object.assign(activeJourney, data);
            }
            vm.editor.init();
            setupListings();
            versionManager(activeJourney);
            state.hasLoaded = true;
        });
    });
</script>

<template>
    <section class="approot workspace">
        <div class="viewport" id="viewport" ref="viewportRef" data-nodetype="viewport">
            <CanvasMain v-if="viewport.el && state.hasLoaded" @click.right.stop.prevent="(ev: MouseEvent) => openquickmenu(ev, true)" />

            <!--
			<CanvasMain v-if="viewport.el" />
            -->

            <ModalContainer v-bind="quickmenu" title="Quickmenu">
                <div class="section-group" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsStartLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsStart" style="--index: 3" />
                </div>
                <div class="section-group mt-l" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsActionsLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsActions" style="--index: 3" />
                </div>
                <div class="section-group mt-l" style="--index: 2">
                    <h3 class="h5">{{ $Resources.NodePresetsConditionsLabel }}</h3>
                    <NodePresetList :columns="4" :gap="8" :list="staticPresetsConditions" style="--index: 3" />
                </div>
            </ModalContainer>

            <ContextMenu v-bind="contextmenu" />

            <ContextMenuContainer :eventController="editor.events" />
        </div>
        <UserInterface />
    </section>
</template>

<style lang="less">
    body:has(.workspace) {
        overflow: hidden;
    }

    .workspace {
        display: grid;
        position: relative;
        grid-template-columns: auto var(--editor-edit-pane-nav-width);

        top: 0;
        left: 0;
        height: 100vh;

        will-change: width, height;
        contain: content;
    }

    .viewport {
        display: block;
        position: relative;
        height: 100%;
        width: 100%;
        flex-grow: 0;
        flex-shrink: 0;
        background-color: #f5f5f5;
        background: -webkit-radial-gradient(circle at 50% 50%, #f7f7f7 70%, #eeeeee);
        background: radial-gradient(circle at 50% 50%, #f7f7f7 70%, #eeeeee);

        perspective: 1000px; /* set this with js in vp settings */

        will-change: width, height;
        contain: content;
    }

    .scr-xy {
        overflow: auto;
    }
</style>
